/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN
import { Card, Tooltip, Button, Input, Tag, Typography } from "antd";
// FONT AWESOME
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faElevator, faLightbulb, faSave } from "@fortawesome/free-solid-svg-icons";
// ANT ICONS
import { HolderOutlined } from "@ant-design/icons";
// DND KIT
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
// REDUX
import { useSelector } from "react-redux";
import { usePutBankMutation } from "../../../redux/banks/bankAPI";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";
// INTERFACES
import { BankInterface } from "../../../interfaces/Bank.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";

const { Text } = Typography;

function SortableLiftItem({
	lift,
	onChangeName,
}: {
	lift: LiftInterface;
	onChangeName: (newValue: string, lift: LiftInterface) => void;
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lift.id });
	const { theme } = useSelector((state: RootState) => state.home);

	const dndStyle: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		userSelect: "none",
		opacity: isDragging ? 0.85 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={{
				...dndStyle,
				border: "1px solid rgba(0,0,0,0.10)",
				borderRadius: 12,
				padding: "10px 12px",
			}}
			className={`layout__contentPage__${theme}`}
			{...attributes}
		>
			<div className='generalStyles__spaceBetween'>
				<div className='generalStyles__flex generalStyles__width100'>
					<div className='generalStyles__flexCenter generalStyles__width50px'>
						<span
							{...listeners}
							style={{
								cursor: "grab",
								padding: 6,
								borderRadius: 8,
							}}
							aria-label='Drag handle'
						>
							<HolderOutlined />
						</span>
					</div>

					<div className='generalStyles__width100'>
						<div className='generalStyles__width100'>
							<Input
								className='generalStyles__width100'
								value={lift.name}
								onChange={(e) => onChangeName(e.target.value, lift)}
							/>
						</div>

						<div style={{ marginTop: 6 }}>
							<Tag color='magenta' className='mt-2'>
								# {lift.position}
							</Tag>
							<span style={{ fontSize: "10px", color: "gray" }}>Lift position</span>
							<span style={{ fontSize: "10px", color: "gray" }}>ID: {lift.id}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function BanksLiftsDetail({
	bank,
	isLoadingGetOneBank,
}: {
	bank: BankInterface;
	isLoadingGetOneBank: boolean;
}) {
	// Local state (editable order)
	const [lifts, setLifts] = useState<LiftInterface[]>(bank.lifts ?? []);

	// ids para dnd
	const ids = useMemo(() => lifts.map((l) => l.id), [lifts]);

	// API
	const [
		triggerPutBank,
		{
			isSuccess: isSuccessPutBank,
			isError: isErrorPutBank,
			error: errorPutBank,
			isLoading: isLoadingPutBank,
			reset: resetPutBank,
		},
	] = usePutBankMutation();

	useEffect(() => {
		const initial = [...(bank.lifts ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
		setLifts(initial);
	}, [bank.lifts]);

	const onClickSave = async () => {
		const token = await GETJwtToken();
		const copy = structuredClone(bank);
		delete copy.data_blocks;
		copy.lifts = lifts;
		triggerPutBank({ token, body: copy, id: bank.id });
		resetPutBank();
	};

	const onChangeName = (newValue: string, lift: LiftInterface) => {
		const copy = structuredClone(lifts);
		const index = copy.findIndex((x) => x.id === lift.id);
		if (index >= 0) {
			copy[index].name = newValue;
			setLifts(copy);
		}
	};

	return (
		<DndContext
			collisionDetection={closestCenter}
			onDragEnd={({ active, over }) => {
				if (!over || active.id === over.id) return;

				setLifts((prev) => {
					const oldIndex = prev.findIndex((l) => l.id === active.id);
					const newIndex = prev.findIndex((l) => l.id === over.id);

					const moved = arrayMove(prev, oldIndex, newIndex);

					const updated = moved.map((l, idx) => ({
						...l,
						position: idx,
					}));

					return updated;
				});
			}}
		>
			<Card
				loading={isLoadingGetOneBank || isLoadingPutBank}
				className='bankFloorsFriendly'
				size='small'
				title={
					<div className='generalStyles__spaceBetween'>
						<div style={{ paddingTop: "8px", marginBottom: "5px" }}>
							<FontAwesomeIcon className='generalStyles__mrFix generalStyles__info' icon={faElevator} />
							<span>Lifts</span>
						</div>
						<div>
							<Tooltip title='save'>
								<Button onClick={onClickSave} type='primary' icon={<FontAwesomeIcon icon={faSave} />} />
							</Tooltip>
						</div>
					</div>
				}
				bordered
			>
				<div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
					<div
						style={{
							width: 10,
							borderRadius: 999,
							background: "rgba(0,0,0,0.08)",
						}}
					/>

					<SortableContext items={ids} strategy={verticalListSortingStrategy}>
						<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
							{lifts.map((lift) => (
								<SortableLiftItem key={lift.id} lift={lift} onChangeName={onChangeName} />
							))}
						</div>
					</SortableContext>
				</div>

				<div style={{ marginTop: 10 }}>
					<FontAwesomeIcon className='generalStyles__mrFix generalStyles__warning' icon={faLightbulb} />

					<Text type='secondary'>Tip: Drag and drop to reorder lifts.</Text>
				</div>
				<GlobalAlert2
					isError={isErrorPutBank}
					isSuccess={isSuccessPutBank}
					requestType='PUT'
					error={errorPutBank}
					name='Bank'
				/>
			</Card>
		</DndContext>
	);
}
